import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import VendorMap from '../components/Map'
import VendorCard from '../components/VendorCard'
import VendorDetail from '../components/VendorDetail'

export default function Explore() {
  const { profile } = useAuth()
  const [schedules, setSchedules] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedVendorId, setSelectedVendorId] = useState(null)
  const [hoveredVendorId, setHoveredVendorId] = useState(null)
  const [detailVendor, setDetailVendor] = useState(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [dayFilter, setDayFilter] = useState('')
  const [facilityFilter, setFacilityFilter] = useState('')

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  useEffect(() => {
    fetchSchedules()
  }, [])

  useEffect(() => {
    groupAndFilterVendors()
  }, [schedules, searchQuery, dayFilter, facilityFilter, profile])

  const fetchSchedules = async () => {
    try {
      const response = await fetch('/.netlify/functions/get-schedules')
      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch schedules')
      }

      setSchedules(result.data || [])
    } catch (error) {
      console.error('Error fetching schedules:', error)
    } finally {
      setLoading(false)
    }
  }

  const groupAndFilterVendors = () => {
    let filtered = [...schedules]

    // Apply filters
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(s =>
        s.applicant?.toLowerCase().includes(query) ||
        s.location_description?.toLowerCase().includes(query) ||
        s.vendors?.food_items?.toLowerCase().includes(query)
      )
    }

    if (dayFilter) {
      filtered = filtered.filter(s => s.day_of_week === dayFilter)
    }

    if (facilityFilter) {
      filtered = filtered.filter(s => s.facility_type === facilityFilter)
    }

    // Group by vendor (applicant name)
    const vendorMap = new Map()
    for (const schedule of filtered) {
      const key = schedule.applicant
      if (!vendorMap.has(key)) {
        // Calculate distance to closest location
        let minDistance = null
        if (profile?.lat && profile?.lng) {
          minDistance = haversineDistance(
            profile.lat,
            profile.lng,
            schedule.lat,
            schedule.lng
          )
        }
        vendorMap.set(key, {
          id: key,
          applicant: schedule.applicant,
          vendor_permit: schedule.vendor_permit,
          vendors: schedule.vendors,
          facility_type: schedule.facility_type,
          schedules: [],
          distance_miles: minDistance,
          lat: schedule.lat,
          lng: schedule.lng
        })
      }

      const vendor = vendorMap.get(key)
      vendor.schedules.push(schedule)

      // Update to closest distance
      if (profile?.lat && profile?.lng) {
        const dist = haversineDistance(profile.lat, profile.lng, schedule.lat, schedule.lng)
        if (vendor.distance_miles === null || dist < vendor.distance_miles) {
          vendor.distance_miles = dist
          vendor.lat = schedule.lat
          vendor.lng = schedule.lng
        }
      }
    }

    // Convert to array and sort
    let vendorList = Array.from(vendorMap.values())
    vendorList.sort((a, b) => {
      if (a.distance_miles !== null && b.distance_miles !== null) {
        return a.distance_miles - b.distance_miles
      }
      return (a.applicant || '').localeCompare(b.applicant || '')
    })

    setVendors(vendorList)
  }

  const facilityTypes = [...new Set(schedules.map(s => s.facility_type).filter(Boolean))]

  const handleCardClick = (vendor) => {
    setSelectedVendorId(vendor.id)
    setDetailVendor(vendor)
  }

  const handleMarkerClick = (schedule) => {
    // Find the vendor for this schedule
    const vendor = vendors.find(v => v.applicant === schedule.applicant)
    if (vendor) {
      setSelectedVendorId(vendor.id)
      setDetailVendor(vendor)
      const cardElement = document.getElementById(`vendor-${vendor.id}`)
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setDayFilter('')
    setFacilityFilter('')
  }

  const hasFilters = searchQuery || dayFilter || facilityFilter
  // Get all schedules from filtered vendors for the map
  const allFilteredSchedules = vendors.flatMap(v => v.schedules)

  return (
    <>
      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-input">
          <svg
            className="filter-input-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Search vendors, food, locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={dayFilter}
          onChange={(e) => setDayFilter(e.target.value)}
        >
          <option value="">All Days</option>
          {days.map(day => (
            <option key={day} value={day}>{day}</option>
          ))}
        </select>

        <select
          className="filter-select"
          value={facilityFilter}
          onChange={(e) => setFacilityFilter(e.target.value)}
        >
          <option value="">All Types</option>
          {facilityTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
            Clear
          </button>
        )}

        <div className="filter-results">
          {loading ? 'Loading...' : `${vendors.length} vendors`}
        </div>
      </div>

      {/* Split Layout */}
      <div className="split-layout">
        {/* List Panel */}
        <div className="split-list">
          <div className="list-header">
            <div className="list-header-title">Food Vendors in San Francisco</div>
            <div className="list-header-subtitle">
              {vendors.length} vendors
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <div className="spinner"></div>
            </div>
          ) : vendors.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.3-4.3"/>
                </svg>
              </div>
              <h3>No results found</h3>
              <p>Try adjusting your filters</p>
              {hasFilters && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={clearFilters}
                  style={{ marginTop: '16px' }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div>
              {vendors.slice(0, 100).map((vendor) => (
                <div key={vendor.id} id={`vendor-${vendor.id}`}>
                  <VendorCard
                    vendor={vendor}
                    onClick={() => handleCardClick(vendor)}
                    isActive={selectedVendorId === vendor.id}
                    onMouseEnter={() => setHoveredVendorId(vendor.id)}
                    onMouseLeave={() => setHoveredVendorId(null)}
                  />
                </div>
              ))}
              {vendors.length > 100 && (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: '13px'
                }}>
                  Showing 100 of {vendors.length} vendors.
                  Use filters to narrow down.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Map Panel */}
        <div className="split-map">
          <VendorMap
            schedules={allFilteredSchedules}
            onMarkerClick={handleMarkerClick}
            userLocation={profile?.lat && profile?.lng ? { lat: profile.lat, lng: profile.lng } : null}
            selectedScheduleId={selectedVendorId}
            hoveredScheduleId={hoveredVendorId}
          />
        </div>
      </div>

      {detailVendor && (
        <VendorDetail
          vendor={detailVendor}
          onClose={() => setDetailVendor(null)}
        />
      )}
    </>
  )
}

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3959
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function toRad(deg) {
  return deg * (Math.PI / 180)
}
